import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { FaLock, FaEnvelope, FaEye, FaEyeSlash, FaShieldAlt } from "react-icons/fa";
import API from "../api";

const particles = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  size: Math.random() * 4 + 2,
  x: Math.random() * 100,
  duration: Math.random() * 15 + 10,
  delay: Math.random() * 5,
}));

export default function AdminLogin({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await API.post("/auth/login", form);
      localStorage.setItem("adminToken", data.token);
      localStorage.setItem("adminUser", JSON.stringify(data.admin));
      onLogin(data.admin);
    } catch (err) {
      setError(err.response?.data?.message || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#060b18] flex items-center justify-center p-4 overflow-hidden relative">

      {/* Animated background blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <motion.div animate={{ scale: [1, 1.2, 1], opacity: [0.15, 0.25, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute -top-40 -left-40 w-96 h-96 rounded-full"
          style={{ background: "radial-gradient(circle, #0ea5e9, transparent 70%)" }} />
        <motion.div animate={{ scale: [1.2, 1, 1.2], opacity: [0.1, 0.2, 0.1] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
          className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full"
          style={{ background: "radial-gradient(circle, #8b5cf6, transparent 70%)" }} />
        <motion.div animate={{ scale: [1, 1.3, 1], opacity: [0.08, 0.15, 0.08] }}
          transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 4 }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{ background: "radial-gradient(circle, #0ea5e920, transparent 70%)" }} />
      </div>

      {/* Floating particles */}
      {mounted && particles.map((p) => (
        <motion.div key={p.id}
          className="absolute rounded-full opacity-30"
          style={{ width: p.size, height: p.size, left: `${p.x}%`, bottom: "-10px", background: "linear-gradient(135deg, #0ea5e9, #8b5cf6)" }}
          animate={{ y: [0, -window.innerHeight - 100], opacity: [0, 0.6, 0] }}
          transition={{ duration: p.duration, repeat: Infinity, delay: p.delay, ease: "linear" }}
        />
      ))}

      {/* Grid pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none"
        style={{ backgroundImage: "linear-gradient(rgba(14,165,233,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.5) 1px, transparent 1px)", backgroundSize: "50px 50px" }} />

      <motion.div initial={{ opacity: 0, y: 40, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative w-full max-w-md z-10">

        {/* Glow behind card */}
        <div className="absolute inset-0 rounded-3xl blur-2xl opacity-20"
          style={{ background: "linear-gradient(135deg, #0ea5e9, #8b5cf6)", transform: "scale(1.05)" }} />

        <div className="relative rounded-3xl overflow-hidden border border-white/10"
          style={{ background: "rgba(10,15,30,0.85)", backdropFilter: "blur(20px)" }}>

          {/* Top gradient bar */}
          <div className="h-1 w-full gradient-bg" />

          <div className="p-8">
            {/* Logo section */}
            <div className="text-center mb-8">
              <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
                className="relative inline-block mb-5">
                <div className="w-20 h-20 gradient-bg rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-blue-500/30">
                  <img src="/circle logo.jpeg" alt="ONexvora" className="w-14 h-14 object-cover rounded-xl" />
                </div>
                {/* Pulse ring */}
                <motion.div animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-2xl gradient-bg opacity-30" />
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <h1 className="text-3xl font-black text-white tracking-tight">
                  ONE<span className="gradient-text">XVORA</span>
                </h1>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="h-px flex-1 bg-gradient-to-r from-transparent to-blue-500/50" />
                  <span className="text-gray-400 text-xs font-medium tracking-widest uppercase px-2">Admin Portal</span>
                  <div className="h-px flex-1 bg-gradient-to-l from-transparent to-purple-500/50" />
                </div>
              </motion.div>
            </div>

            {/* Form */}
            <motion.form onSubmit={handleSubmit} className="space-y-4"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>

              <div>
                <label className="text-gray-400 text-xs mb-1.5 block font-medium">Email Address</label>
                <div className="relative group">
                  <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm group-focus-within:text-blue-400 transition-colors" />
                  <input type="email" required value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder="admin@onexvora.com"
                    className="w-full border rounded-xl pl-10 pr-4 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 text-sm transition-all"
                    style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
                    onFocus={(e) => e.target.style.background = "rgba(14,165,233,0.05)"}
                    onBlur={(e) => e.target.style.background = "rgba(255,255,255,0.04)"}
                  />
                </div>
              </div>

              <div>
                <label className="text-gray-400 text-xs mb-1.5 block font-medium">Password</label>
                <div className="relative group">
                  <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm group-focus-within:text-blue-400 transition-colors" />
                  <input type={showPass ? "text" : "password"} required value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    placeholder="••••••••••"
                    className="w-full border rounded-xl pl-10 pr-12 py-3.5 text-white placeholder-gray-600 focus:outline-none focus:border-blue-500 text-sm transition-all"
                    style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.1)" }}
                    onFocus={(e) => e.target.style.background = "rgba(14,165,233,0.05)"}
                    onBlur={(e) => e.target.style.background = "rgba(255,255,255,0.04)"}
                  />
                  <button type="button" onClick={() => setShowPass(!showPass)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
                    {showPass ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                  className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  <div className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0" />
                  <p className="text-red-400 text-xs">{error}</p>
                </motion.div>
              )}

              <motion.button type="submit" disabled={loading}
                whileHover={{ scale: loading ? 1 : 1.02 }} whileTap={{ scale: loading ? 1 : 0.98 }}
                className="btn-primary w-full py-3.5 rounded-xl text-white font-bold text-sm disabled:opacity-60 flex items-center justify-center gap-2 mt-2 shadow-lg shadow-blue-500/20">
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Authenticating...</span>
                  </>
                ) : (
                  <>
                    <FaShieldAlt className="text-xs" />
                    <span>Access Dashboard</span>
                  </>
                )}
              </motion.button>
            </motion.form>

            {/* Footer */}
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
              className="text-center text-gray-600 text-xs mt-6">
              🔒 Secured & Encrypted Access
            </motion.p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
